/*
 * Copyright 2022. NXGN Management, LLC. All Rights Reserved.
 */

import { Component, Input, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService, MerchantService, NotificationService, SharedFormService } from "../../../../services";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Merchant } from "../../../../models";
import { MerchantDetailsParentComponent } from "../../merchant-details-parent.component";
import { MERCHANT_CONSTANTS, PATTERN_CONSTANTS } from "../../../../utils/data.rest.constants";

@Component({
  selector: "app-wp-general-info",
  templateUrl: "./wp-general-info.component.html"
})
export class WpGeneralInfoComponent extends MerchantDetailsParentComponent implements OnInit {
  editable = false;
  wpGeneralInfoForm!: FormGroup;
  buttonName = "Update Merchant General Info";
  actionInProgress = false;
  @Input("merchant") merchant!: Merchant;

  constructor(
    router: Router,
    activatedRoute: ActivatedRoute,
    merchantService: MerchantService,
    notificationService: NotificationService,
    authService: AuthService,
    private fb: FormBuilder,
    private sharedFormService: SharedFormService
  ) {
    super(router, activatedRoute, merchantService, notificationService, authService);
  }

  ngOnInit(): void {
    this.createForm();
  }

  createForm() {
    this.wpGeneralInfoForm = this.fb.group({
      generalInfo: this.fb.group({
        merchantName: [this.merchant.merchantName, [Validators.required, Validators.maxLength(50)]],
        doingBusinessAs: [this.merchant.doingBusinessAs, [Validators.required, Validators.maxLength(99)]],
        alsoKnownAsName: [this.merchant.alsoKnownAsName],
        externalMerchantId: [this.merchant.externalMerchantId, [Validators.maxLength(12)]],
        customerAccountNumber: [this.merchant.customerAccountNumber, Validators.required],
        phoneNumber: [
          this.merchant.phoneNumber,
          [Validators.pattern(PATTERN_CONSTANTS.NUMBER_10), Validators.required]
        ],
        maxTransactionLimit: [
          this.merchant.maxTransactionLimit / 100,
          [Validators.pattern(PATTERN_CONSTANTS.MONEY), Validators.required, Validators.min(0)]
        ],
        billingDescriptor: [
          this.merchant.accountDetails.worldPayAccountDetails.billingDescriptor,
          [Validators.required, Validators.maxLength(25)]
        ],
        ownershipType: [
          {
            value:
              this.merchant.accountDetails.worldPayAccountDetails.ownershipType !== null
                ? this.merchant.accountDetails.worldPayAccountDetails.ownershipType
                : "",
            disabled: true
          },
          Validators.required
        ],
        businessType: [this.merchant.accountDetails.worldPayAccountDetails.businessType, Validators.required],
        businessEstablishedDate: [
          new Date(this.merchant.accountDetails.worldPayAccountDetails.businessEstablishedDate)
        ],
        websiteURL: [
          this.merchant.accountDetails.worldPayAccountDetails.websiteURL,
          [
            Validators.required,
            Validators.pattern("^(http[s]?://){0,1}(www.){0,1}[a-zA-Z0-9.-]+.[a-zA-Z]{2,5}[.]{0,1}")
          ]
        ],
        mccCode: [
          this.merchant.accountDetails.worldPayAccountDetails.mccCode,
          [Validators.required, Validators.maxLength]
        ],
        payApiCustomers: [this.merchant.payAPICustomer]
      })
    });
  }

  updateElementMerchant(): void {
    if (this.generalInfoFormGroup.valid) {
      this.actionInProgress = true;
      this.setFeeDetailsToNullIfNoneExist(this.merchant);
      const tempMerchant: Merchant = JSON.parse(JSON.stringify(this.merchant));
      this.sharedFormService.updateMerchantGeneralInfo(tempMerchant, this.generalInfoFormGroup);
      tempMerchant.statementOptions = {};
      if (!this.authService.getUserRole(MERCHANT_CONSTANTS.FINANCE)) {
        tempMerchant.contractedRates = null as any;
      }
      this.merchantService.updateElementMerchant("GENERAL_INFO", tempMerchant).subscribe(
        (merchant) => {
          this.merchant = merchant;
          this.merchant.payAPICustomer = this.generalInfoFormGroup.get("payApiCustomers")?.value;
          this.editable = false;
          this.actionInProgress = false;
        },
        (error) => {
          this.notificationService.error(error.error);
        }
      );
    } else {
      this.generalInfoFormGroup.markAllAsTouched();
      this.actionInProgress = false;
    }
  }

  setEditable(value: boolean) {
    this.editable = value;
  }

  get generalInfoFormGroup(): FormGroup {
    return this.wpGeneralInfoForm.controls["generalInfo"] as FormGroup;
  }

  receiveEditEvent(value: boolean) {
    this.editable = value;
    this.createForm();
  }
}
